class ShippingCalculator {
  constructor() {
    this.cache = new Map();
    this.listeners = new Set();
    this.currentShippingData = null;
    this.isCalculating = false;
    this.debounceTimer = null;

    // Progress bar state management
    this.progressBarState = {
      percentage: 0,
      message: 'Add items to your basket for free shipping',
    };

    // Debouncer for progress bar creation
    this.initializationDebouncer = null;

    this.defaultPostcodes = {
      GB: 'SW1A 1AA', // UK - Westminster, London
      US: '10001', // US - New York
      CA: 'K1A 0A6', // Canada - Ottawa
      AU: '2000', // Australia - Sydney
      DE: '10115', // Germany - Berlin
      FR: '75001', // France - Paris
      IT: '00118', // Italy - Rome
      ES: '28001', // Spain - Madrid
      NL: '1012', // Netherlands - Amsterdam
      BE: '1000', // Belgium - Brussels
      CH: '3001', // Switzerland - Bern
      AT: '1010', // Austria - Vienna
      SE: '11120', // Sweden - Stockholm
      NO: '0150', // Norway - Oslo
      DK: '1050', // Denmark - Copenhagen
      FI: '00100', // Finland - Helsinki
      IE: 'D01', // Ireland - Dublin
      PT: '1000-001', // Portugal - Lisbon
      PL: '00-001', // Poland - Warsaw
      CZ: '110 00', // Czech Republic - Prague
      HU: '1011', // Hungary - Budapest
      GR: '10431', // Greece - Athens
      JP: '100-0001', // Japan - Tokyo
      KR: '04524', // South Korea - Seoul
      SG: '018956', // Singapore
      HK: '999077', // Hong Kong
      NZ: '6011', // New Zealand - Wellington
      ZA: '8001', // South Africa - Cape Town
      BR: '01310-100', // Brazil - São Paulo
      MX: '06600', // Mexico - Mexico City
      AR: 'C1002', // Argentina - Buenos Aires
      CL: '8320000', // Chile - Santiago
      CO: '110111', // Colombia - Bogotá
      PE: '15001', // Peru - Lima
      IN: '110001', // India - New Delhi
      CN: '100000', // China - Beijing
      TH: '10100', // Thailand - Bangkok
      MY: '50000', // Malaysia - Kuala Lumpur
      PH: '1000', // Philippines - Manila
      ID: '10110', // Indonesia - Jakarta
      VN: '100000', // Vietnam - Hanoi
      TR: '34000', // Turkey - Istanbul
      RU: '101000', // Russia - Moscow
      UA: '01001', // Ukraine - Kyiv
      IL: '9100001', // Israel - Jerusalem
      AE: '00000', // UAE - Dubai
      SA: '11564', // Saudi Arabia - Riyadh
      EG: '11511', // Egypt - Cairo
      MA: '10000', // Morocco - Rabat
      NG: '100001', // Nigeria - Lagos
      KE: '00100', // Kenya - Nairobi
      GH: '00233', // Ghana - Accra
    };

    // Initialize progress bar management
    this.initProgressBarManagement();
  }

  // Initialize progress bar management system
  initProgressBarManagement() {
    // Set up DOM observer to watch for cart drawer container
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if the cart drawer is added
            const cartDrawer = node.tagName === 'CART-DRAWER' ? node : node.querySelector?.('cart-drawer');
            if (cartDrawer) {
              this.debouncedEnsureProgressBar();
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Initialize progress bar immediately if cart drawer exists
    document.addEventListener('DOMContentLoaded', () => {
      this.ensureProgressBarExists();
    });

    // Also check immediately in case DOM is already loaded
    if (document.readyState === 'loading') {
      // DOM is still loading, event listener above will handle it
    } else {
      // DOM is already loaded
      this.ensureProgressBarExists();
    }
  }

  // Debounced progress bar creation to prevent rapid multiple calls
  debouncedEnsureProgressBar() {
    if (this.initializationDebouncer) {
      clearTimeout(this.initializationDebouncer);
    }

    this.initializationDebouncer = setTimeout(() => {
      this.ensureProgressBarExists();
    }, 50); // 50ms debounce
  }

  // Ensure progress bar exists and is properly positioned
  ensureProgressBarExists() {
    // Find the stable cart drawer container (doesn't get replaced)
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return; // Cart drawer not present
    }

    let progressBar = document.getElementById('shipping-progress-bar');

    // If progress bar doesn't exist, create it
    if (!progressBar) {
      progressBar = this.createProgressBarElement();

      // Find the drawer dialog container (stable, doesn't get replaced)
      const drawerDialog = cartDrawer.querySelector('#CartDrawer-Dialog');
      if (drawerDialog) {
        // Find the CartDrawer-Header element and insert the progress bar after it
        const cartHeader = drawerDialog.querySelector('#CartDrawer-Header');
        if (cartHeader && cartHeader.nextSibling) {
          drawerDialog.insertBefore(progressBar, cartHeader.nextSibling);
        } else if (cartHeader) {
          // If no next sibling, append after the header
          cartHeader.insertAdjacentElement('afterend', progressBar);
        } else {
          // Fallback: insert at the very top of the drawer dialog
          drawerDialog.insertBefore(progressBar, drawerDialog.firstChild);
        }
      } else {
        // Fallback: insert in cart drawer root
        cartDrawer.insertBefore(progressBar, cartDrawer.firstChild);
      }

      // Initialize with current state
      this.initializeProgressBarElement(progressBar);
    }
  }

  // Create the progress bar HTML element
  createProgressBarElement() {
    const progressBarContainer = document.createElement('div');
    progressBarContainer.className = 'relative w-full px-4 pb-4 flex flex-col gap-2';
    progressBarContainer.id = 'shipping-progress-bar';

    progressBarContainer.innerHTML = `
      <div class="relative w-full h-6 rounded-full bg-inactive/20 overflow-hidden">
        <div
          id="progress-bar-fill"
          class="h-full bg-highlight transition-all duration-300 ease-in-out"
          style="width: ${this.progressBarState.percentage}%"
        ></div>
      </div>
      <p id="progress-bar-message" class="absolute translate-y-0.5 w-full h-max text-left text-small pl-2">${this.progressBarState.message}</p>
    `;

    return progressBarContainer;
  }

  // Initialize a specific progress bar element
  initializeProgressBarElement(progressBarElement) {
    if (!progressBarElement) return;

    const progressFill = progressBarElement.querySelector('#progress-bar-fill');
    const progressMessage = progressBarElement.querySelector('#progress-bar-message');

    if (progressFill && progressMessage) {
      // Set the current state (element was already created with correct width)
      progressMessage.textContent = this.progressBarState.message;
    }
  }

  // Update the progress bar element
  updateProgressBar(progressData) {
    if (!progressData) return;

    const { percentage, message } = progressData;

    // Update stored state
    this.progressBarState = { percentage, message };

    // Ensure progress bar exists
    this.ensureProgressBarExists();

    // Find the progress bar element
    const progressBarElement = document.getElementById('shipping-progress-bar');
    if (!progressBarElement) return;

    const progressFill = progressBarElement.querySelector('#progress-bar-fill');
    const progressMessage = progressBarElement.querySelector('#progress-bar-message');

    if (progressFill && progressMessage) {
      // Normal smooth transition to new width
      progressFill.style.width = `${percentage}%`;
      progressMessage.textContent = message;
    }
  }

  // Subscribe to shipping updates
  subscribe(callback) {
    this.listeners.add(callback);

    // If we already have shipping data, call the callback immediately
    if (this.currentShippingData) {
      callback(this.currentShippingData);
    }

    return () => this.listeners.delete(callback);
  }

  // Calculate progress bar data based on shipping data
  calculateProgressData(shippingData) {
    let percentage = 0;
    let message = 'Add items to your basket for free shipping';

    if (!shippingData) {
      return { percentage, message };
    }

    if (shippingData.status === 'empty') {
      percentage = 0;
      if (shippingData.country) {
        const threshold = this.getThresholdForCountry(shippingData.country);
        const currency = this.formatCurrency(threshold, shippingData.country);
        message = `Spend ${currency} to get free shipping`;
      } else {
        message = 'Add items to your basket for free shipping';
      }
    } else if (
      shippingData.status === 'unavailable' ||
      shippingData.status === 'error' ||
      shippingData.status === 'postcode_required'
    ) {
      percentage = 0;
      message = 'Shipping calculated at checkout';
    } else if (shippingData.status === 'available') {
      if (shippingData.isFreeShipping) {
        percentage = 100;
        message = 'You qualify for free shipping!';
      } else if (shippingData.freeShippingThreshold && shippingData.amountToFreeShipping) {
        const currentAmount = shippingData.freeShippingThreshold - shippingData.amountToFreeShipping;
        percentage = Math.min(Math.max((currentAmount / shippingData.freeShippingThreshold) * 100, 0), 100);

        const remainingFormatted = this.formatCurrency(shippingData.amountToFreeShipping, shippingData.country);
        message = `Spend ${remainingFormatted} more to get free shipping`;
      } else {
        // No free shipping threshold found, use theme settings fallback calculation
        const fallbackThreshold = this.getThresholdForCountry(shippingData.country);
        const currentAmount = shippingData.cartTotal || 0;

        if (currentAmount >= fallbackThreshold) {
          percentage = 100;
          message = 'You qualify for free shipping!';
        } else if (currentAmount > 0) {
          percentage = (currentAmount / fallbackThreshold) * 100;
          const remaining = fallbackThreshold - currentAmount;
          const remainingFormatted = this.formatCurrency(remaining, shippingData.country);
          message = `Spend ${remainingFormatted} more to get free shipping`;
        } else {
          percentage = 0;
          message = 'Add items to your basket for free shipping';
        }
      }
    }

    return { percentage: Math.round(percentage), message };
  }

  // Notify all subscribers of shipping updates
  notify(shippingData) {
    this.currentShippingData = shippingData;

    // Calculate and add progress data
    const progressData = this.calculateProgressData(shippingData);
    const enrichedData = { ...shippingData, progressData };

    // Update progress bars directly
    this.updateProgressBar(progressData);

    this.listeners.forEach((callback) => callback(enrichedData));
  }

  // Get current country from localization system
  getCurrentCountry() {
    // Try to get from the localization form if it exists
    const localizationForm = document.querySelector('localization-form');
    if (localizationForm) {
      const countryInput = localizationForm.querySelector('input[name="country_code"]');
      if (countryInput && countryInput.value) {
        return countryInput.value;
      }
    }

    // Fallback: try to get from any country localization button
    const countryButton = document.querySelector(
      '[data-country-localization] button[name="country_code"][aria-current="true"]',
    );
    if (countryButton) {
      return countryButton.value;
    }

    // Final fallback: use the server-side localization country (embedded in template)
    const shippingSection = document.getElementById('shipping-cost-section');
    if (shippingSection) {
      const countryCode = shippingSection.dataset.countryCode;
      return countryCode;
    }

    return 'GB'; // Default to GB
  }

  // Calculate shipping for current cart and country
  async calculateShipping(cartData = null, countryCode = null) {
    // Debounce multiple rapid calls
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(async () => {
      await this._performCalculation(cartData, countryCode);
    }, 100); // 100ms debounce
  }

  // Internal method that performs the actual calculation
  async _performCalculation(cartData = null, countryCode = null) {
    // Prevent multiple simultaneous calculations
    if (this.isCalculating) {
      return this.currentShippingData;
    }

    this.isCalculating = true;

    try {
      // Get cart data if not provided
      if (!cartData) {
        const cartResponse = await fetch('/cart.js');
        cartData = await cartResponse.json();
      }

      // Get country if not provided
      if (!countryCode) {
        countryCode = this.getCurrentCountry();
      }

      // Create cache key
      const cacheKey = `${countryCode}-${cartData.total_price}-${cartData.item_count}`;

      // Check cache first
      if (this.cache.has(cacheKey)) {
        const cachedData = this.cache.get(cacheKey);
        this.notify(cachedData);
        return cachedData;
      }

      // If cart is empty, return appropriate data
      if (cartData.item_count === 0) {
        const emptyCartData = {
          country: countryCode,
          cartTotal: 0,
          shippingCost: null,
          isFreeShipping: false,
          freeShippingThreshold: null,
          amountToFreeShipping: null,
          shippingRates: null,
          status: 'empty',
        };
        this.cache.set(cacheKey, emptyCartData);
        this.notify(emptyCartData);
        return emptyCartData;
      }

      // Prepare shipping address
      const shippingAddress = {
        country: countryCode,
        province: '',
        zip: this.defaultPostcodes[countryCode] || '',
        city: '',
        address1: '',
        address2: '',
      };

      // Build query string for shipping rates
      const params = new URLSearchParams({
        'shipping_address[country]': shippingAddress.country,
        'shipping_address[province]': shippingAddress.province,
        'shipping_address[zip]': shippingAddress.zip,
        'shipping_address[city]': shippingAddress.city,
        'shipping_address[address1]': shippingAddress.address1,
        'shipping_address[address2]': shippingAddress.address2,
      });

      // Add cart items to the request
      cartData.items.forEach((item, index) => {
        params.append(`items[${index}][variant_id]`, item.variant_id);
        params.append(`items[${index}][quantity]`, item.quantity);
      });

      const shippingUrl = `/cart/shipping_rates.json?${params.toString()}`;

      // Fetch shipping rates
      const shippingResponse = await fetch(shippingUrl);

      let shippingData = {
        country: countryCode,
        cartTotal: cartData.total_price,
        shippingCost: null,
        isFreeShipping: false,
        freeShippingThreshold: null,
        amountToFreeShipping: null,
        shippingRates: null,
        status: 'unknown',
      };

      if (shippingResponse.ok) {
        const responseData = await shippingResponse.json();
        const shippingRates = responseData.shipping_rates;

        if (Array.isArray(shippingRates) && shippingRates.length > 0) {
          // Find the cheapest shipping option
          const cheapestRate = shippingRates.reduce((min, rate) =>
            parseFloat(rate.price) < parseFloat(min.price) ? rate : min,
          );

          const shippingCostInPence = Math.round(parseFloat(cheapestRate.price) * 100);
          const isFreeShipping = shippingCostInPence === 0;

          shippingData = {
            country: countryCode,
            cartTotal: cartData.total_price,
            shippingCost: shippingCostInPence,
            isFreeShipping,
            freeShippingThreshold: isFreeShipping ? cartData.total_price : null,
            amountToFreeShipping: isFreeShipping ? 0 : null,
            shippingRates,
            cheapestRate,
            status: 'available',
          };

          // If shipping is not free, try to determine the free shipping threshold
          // Only do this for larger carts to avoid too many API calls
          if (!isFreeShipping && cartData.total_price > 1000) {
            await this.findFreeShippingThreshold(shippingData, cartData, countryCode);
          } else if (!isFreeShipping) {
            // For smaller carts, use theme settings fallback threshold
            const fallbackThreshold = this.getThresholdForCountry(countryCode);
            shippingData.freeShippingThreshold = fallbackThreshold;
            shippingData.amountToFreeShipping = fallbackThreshold - cartData.total_price;
          }
        } else {
          shippingData.status = 'unavailable';
        }
      } else {
        const errorText = await shippingResponse.text();
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.zip && errorData.zip.includes('postcode')) {
            shippingData.status = 'postcode_required';
          } else {
            shippingData.status = 'unavailable';
          }
        } catch (e) {
          shippingData.status = 'error';
        }
      }

      // Cache the result
      this.cache.set(cacheKey, shippingData);

      // Notify subscribers
      this.notify(shippingData);

      return shippingData;
    } catch (error) {
      console.error('Shipping calculation error:', error);
      const errorData = {
        country: countryCode || this.getCurrentCountry(),
        cartTotal: cartData ? cartData.total_price : 0,
        shippingCost: null,
        isFreeShipping: false,
        freeShippingThreshold: null,
        amountToFreeShipping: null,
        shippingRates: null,
        status: 'error',
      };
      this.notify(errorData);
      return errorData;
    } finally {
      this.isCalculating = false;
    }
  }

  // Try to find free shipping threshold by testing different cart values
  async findFreeShippingThreshold(shippingData, cartData, countryCode) {
    const currentTotal = cartData.total_price;

    // Test common free shipping thresholds (in pence) - limited set to reduce API calls
    const testThresholds = [3000, 4000, 5000, 6000, 7500, 10000];

    for (const threshold of testThresholds) {
      if (threshold <= currentTotal) continue; // Skip thresholds we've already passed

      try {
        // Create a test cart with increased value
        const testCartData = {
          ...cartData,
          total_price: threshold,
        };

        const cacheKey = `${countryCode}-${threshold}-${cartData.item_count}`;

        // Check cache first
        if (this.cache.has(cacheKey)) {
          const cachedResult = this.cache.get(cacheKey);
          if (cachedResult.isFreeShipping) {
            shippingData.freeShippingThreshold = threshold;
            shippingData.amountToFreeShipping = threshold - currentTotal;
            break;
          }
          continue;
        }

        // Build shipping request for test amount
        const params = new URLSearchParams({
          'shipping_address[country]': countryCode,
          'shipping_address[province]': '',
          'shipping_address[zip]': this.defaultPostcodes[countryCode] || '',
          'shipping_address[city]': '',
          'shipping_address[address1]': '',
          'shipping_address[address2]': '',
        });

        // Add cart items but with adjusted prices to reach threshold
        const adjustmentPerItem = Math.ceil((threshold - currentTotal) / cartData.items.length);

        cartData.items.forEach((item, index) => {
          params.append(`items[${index}][variant_id]`, item.variant_id);
          params.append(`items[${index}][quantity]`, item.quantity);
          // Note: We can't actually adjust item prices in the API call,
          // so this is a limitation. We'll use common thresholds instead.
        });

        // For now, we'll use common known thresholds rather than testing each one
        // as the Shopify API doesn't allow us to modify item prices dynamically
        break;
      } catch (error) {
        console.warn('Error testing threshold:', threshold, error);
        continue;
      }
    }

    // If we couldn't find a specific threshold, use theme settings or defaults
    if (!shippingData.freeShippingThreshold) {
      const defaultThreshold = this.getThresholdForCountry(countryCode);

      if (currentTotal < defaultThreshold) {
        shippingData.freeShippingThreshold = defaultThreshold;
        shippingData.amountToFreeShipping = defaultThreshold - currentTotal;
      }
    }
  }

  // Get threshold for country from theme settings
  getThresholdForCountry(countryCode) {
    const themeSettings = window.theme?.settings;

    // Fallback values if theme settings aren't available
    const fallbackThresholds = {
      uk: 5000, // £50
      eu: 5000, // €50
      us: 7500, // $75
      row: 5000, // £50
    };

    const thresholds = {
      uk: themeSettings?.free_shipping_threshold_uk || fallbackThresholds.uk,
      eu: themeSettings?.free_shipping_threshold_eu || fallbackThresholds.eu,
      us: themeSettings?.free_shipping_threshold_us || fallbackThresholds.us,
      row: themeSettings?.free_shipping_threshold_row || fallbackThresholds.row,
    };

    const euCountries = [
      'DE',
      'FR',
      'IT',
      'ES',
      'NL',
      'BE',
      'AT',
      'SE',
      'DK',
      'FI',
      'IE',
      'PT',
      'PL',
      'CZ',
      'HU',
      'GR',
    ];
    const usCountries = ['US', 'CA', 'AU']; // Group similar currency regions

    if (countryCode === 'GB') {
      return thresholds.uk;
    } else if (usCountries.includes(countryCode)) {
      return thresholds.us;
    } else if (euCountries.includes(countryCode)) {
      return thresholds.eu;
    } else {
      return thresholds.row;
    }
  }

  // Clear cache (useful when cart changes significantly)
  clearCache() {
    this.cache.clear();
  }

  // Format currency based on country
  formatCurrency(amountInPence, countryCode = null) {
    const country = countryCode || this.getCurrentCountry();

    const currencyMap = {
      GB: { currency: 'GBP', locale: 'en-GB' },
      US: { currency: 'USD', locale: 'en-US' },
      CA: { currency: 'CAD', locale: 'en-CA' },
      AU: { currency: 'AUD', locale: 'en-AU' },
      DE: { currency: 'EUR', locale: 'de-DE' },
      FR: { currency: 'EUR', locale: 'fr-FR' },
      IT: { currency: 'EUR', locale: 'it-IT' },
      ES: { currency: 'EUR', locale: 'es-ES' },
      NL: { currency: 'EUR', locale: 'nl-NL' },
      BE: { currency: 'EUR', locale: 'nl-BE' },
      AT: { currency: 'EUR', locale: 'de-AT' },
      SE: { currency: 'SEK', locale: 'sv-SE' },
      NO: { currency: 'NOK', locale: 'nb-NO' },
      DK: { currency: 'DKK', locale: 'da-DK' },
      FI: { currency: 'EUR', locale: 'fi-FI' },
      IE: { currency: 'EUR', locale: 'en-IE' },
      PT: { currency: 'EUR', locale: 'pt-PT' },
      PL: { currency: 'PLN', locale: 'pl-PL' },
      CZ: { currency: 'CZK', locale: 'cs-CZ' },
      HU: { currency: 'HUF', locale: 'hu-HU' },
      GR: { currency: 'EUR', locale: 'el-GR' },
      JP: { currency: 'JPY', locale: 'ja-JP' },
      KR: { currency: 'KRW', locale: 'ko-KR' },
    };

    const { currency, locale } = currencyMap[country] || { currency: 'GBP', locale: 'en-GB' };

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amountInPence / 100);
  }
}

// Create a global instance
if (!window.shippingCalculator) {
  window.shippingCalculator = new ShippingCalculator();
}
